import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { resilientFetch } from "./lib/resilientFetch";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;
const TOKEN_KEY = "hedaply_token";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown / not logged in
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await resilientFetch(`${API}/auth/me`, { headers: authHeaders() });
      if (res.status === 401) {
        // Genuine auth failure — token revoked or expired. Sign out locally.
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      } else if (res.ok) {
        setUser(await res.json());
      } else {
        // 502 / 503 / 504 — transient origin error. Keep the token so the
        // user stays logged in once the backend is healthy again. Just
        // surface as "not logged in yet" for this render cycle.
        setUser(null);
      }
    } catch {
      // Hard network failure even after retries — keep the token, treat as
      // temporarily-offline so the user isn't kicked out by a WiFi blip.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Login failed");
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: authHeaders(),
      });
    } catch {
      // ignore network errors — local sign-out still proceeds
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}
